import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import Iconify from './Iconify';
import SplitMeter from './SplitMeter';
import { DUR, EASE_OUT, STAGGER } from '../utils/motion';
import { LABEL_MICRO_ON_RED, LABEL_MICRO_ON_YELLOW, NUMERAL_MD } from '../utils/typeScale';
import { RED, YELLOW, DARK_TEXT } from '../utils/brandColors';

/**
 * Per-archetype identity. The four keys are the complete set returned by
 * backend `computeArchetype`, but the map is read through a `default` fallback
 * so an unlisted or re-cased string never renders `undefined` as a background
 * colour.
 */
export const ARCHETYPE = {
  'The Oracle': { field: RED, text: '#fff', icon: 'eva:eye-outline' },
  'Hot Take Maestro': { field: YELLOW, text: DARK_TEXT, icon: 'eva:flash-outline' },
  'Pulse Reader': { field: RED, text: '#fff', icon: 'eva:activity-outline' },
  'Contrarian Realist': { field: YELLOW, text: DARK_TEXT, icon: 'eva:compass-outline' },
  default: { field: RED, text: '#fff', icon: 'eva:eye-outline' },
};

export function archetypeIdentity(name) {
  return ARCHETYPE[name] || ARCHETYPE.default;
}

EvidenceBar.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  onRed: PropTypes.bool,
  delay: PropTypes.number,
  reducedMotion: PropTypes.bool,
};

function EvidenceBar({ label, value = 0, onRed = true, delay = 0, reducedMotion = false }) {
  // Count up from zero on open so the number and the bar resolve together.
  // Reduced motion jumps straight to the target.
  const [shown, setShown] = useState(reducedMotion ? value : 0);

  useEffect(() => {
    if (reducedMotion) {
      setShown(value);
      return undefined;
    }
    setShown(0);
    const start = performance.now() + delay;
    let frame;
    const step = (now) => {
      const elapsed = now - start;
      if (elapsed < 0) {
        frame = requestAnimationFrame(step);
        return;
      }
      const progress = Math.min(1, elapsed / DUR.count);
      // easeOutCubic — matches the EASE_OUT feel of the bar fill.
      setShown(Math.round(value * (1 - (1 - progress) ** 3)));
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, delay, reducedMotion]);

  const labelSx = onRed ? LABEL_MICRO_ON_RED : LABEL_MICRO_ON_YELLOW;
  const numeralColor = onRed ? '#fff' : DARK_TEXT;
  const track = onRed ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.18)';
  const fill = onRed ? '#fff' : DARK_TEXT;

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography sx={labelSx}>{label}</Typography>
        <Typography sx={{ ...NUMERAL_MD, color: numeralColor }}>{shown}%</Typography>
      </Stack>
      {/* `reducedMotion` is passed unconditionally, which reads odd but is
          deliberate: it only suppresses the meter's own CSS width transition,
          and the rAF loop above already animates `shown` on an easeOutCubic.
          Leaving the transition on would restart a 260ms tween every frame,
          so the bar would crawl in well behind the numeral it evidences. */}
      <SplitMeter
        percentA={shown}
        height={4}
        fillColor={fill}
        trackColor={track}
        reducedMotion
      />
    </Box>
  );
}

ArchetypeBlock.propTypes = {
  archetype: PropTypes.string,
  metrics: PropTypes.object,
};

/**
 * The archetype as identity, not as a default MUI Chip. A flat full-bleed
 * brand field — the strongest use of colour in the app — with the two metrics
 * that justify the label rendered directly beneath it as evidence.
 */
export default function ArchetypeBlock({ archetype, metrics }) {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const name = archetype || 'Pulse Reader';
  const identity = archetypeIdentity(name);
  const onRed = identity.text === '#fff';

  // The two metrics ship in different units and always have:
  // voteStreak.ts:132 writes `minorityVoteRatio` as a 0–1 ratio, :133 writes
  // `predictionAccuracyPct` already as 0–100. Convert only the former.
  const minority = Math.round(Math.min(1, Math.max(0, metrics?.minority_vote_ratio ?? 0)) * 100);
  const accuracy = Math.round(Math.min(100, Math.max(0, metrics?.prediction_accuracy_pct ?? 0)));

  return (
    <Box sx={{ borderRadius: '12px', p: 2.5, bgcolor: identity.field, color: identity.text }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Iconify icon={identity.icon} width={18} height={18} sx={{ color: identity.text }} />
        <Typography sx={onRed ? LABEL_MICRO_ON_RED : LABEL_MICRO_ON_YELLOW}>Your archetype</Typography>
      </Stack>
      <Typography
        sx={{
          fontSize: { xs: 28, sm: 32 },
          fontWeight: 900,
          letterSpacing: '-0.02em',
          mt: 0.5,
          color: identity.text,
        }}
      >
        {name}
      </Typography>

      <Stack
        spacing={1.5}
        sx={{
          mt: 2.5,
          // One-shot reveal; the bars fill from the evidence count-up.
          animation: prefersReducedMotion ? 'none' : `evidenceIn ${DUR.reveal}ms ${EASE_OUT} both`,
          '@keyframes evidenceIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      >
        <EvidenceBar
          label="Minority picks"
          value={minority}
          onRed={onRed}
          reducedMotion={prefersReducedMotion}
        />
        <EvidenceBar
          label="Prediction accuracy"
          value={accuracy}
          onRed={onRed}
          delay={STAGGER}
          reducedMotion={prefersReducedMotion}
        />
      </Stack>
    </Box>
  );
}
